import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import Checkbox, { SIZES } from 'components/Checkbox';
import InlineLink from 'components/InlineLink';
import Input from 'components/Input';
import RichText from 'components/RichText';
import { MODULE_TYPES, PAGE_SECTIONS_V3 } from 'constants/homepage';
import I18n from 'utils/I18n';
import { cn } from 'utils/Tailwind';
import Validator from 'utils/Validator';

export const BACKGROUND_COLORS = {
  BLACK: 'bg-black',
  WHITE: 'bg-white',
  BEIGE: 'bg-grey',
};

const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <span className="text-sm">{children}</span>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink linkTo={node.data.uri} className="text-sm">
        {children}
      </InlineLink>
    ),
  },
};

const EmailSignupModule = ({
  backgroundColor = BACKGROUND_COLORS.BEIGE,
  disclaimer = {},
  homepagePosition,
  marketingMessage = '',
  moduleName = PAGE_SECTIONS_V3.EMAIL_SIGNUP_MODULE,
  onSubmit = () => {},
  text = {},
  trackCTAClick = () => {},
}) => {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [acceptsMarketing, setAcceptsMarketing] = useState(true);
  const [moduleHeight, setModuleHeight] = useState(0);
  const moduleRef = useRef(null);
  const isInverted = backgroundColor === 'Black';

  useEffect(() => {
    if (moduleRef.current) {
      setModuleHeight(moduleRef.current.clientHeight);
    }
  }, []);

  const handleMarketingMessageChange = () => {
    setAcceptsMarketing(prev => !prev);
  };

  const handleOnChange = useCallback(value => {
    setEmail(value);
    setIsValid(null);
  }, []);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      const valid = Validator.isEmail(email);
      setIsValid(valid);
      if (valid) {
        setHasSubmitted(true);
        onSubmit(email, acceptsMarketing);
        trackCTAClick({
          linkCTAName: I18n.localize('emailCapture.signUp'),
          moduleName,
          modulePosition: homepagePosition,
          moduleType: MODULE_TYPES.EMAIL_SIGNUP_MODULE,
        });
      }
    },
    [acceptsMarketing, email, homepagePosition, moduleName, onSubmit, trackCTAClick]
  );

  return (
    <div
      className={cn(
        'EmailSignupModule flex items-center justify-center',
        BACKGROUND_COLORS[backgroundColor.toUpperCase()]
      )}
      style={{ minHeight: moduleHeight }}
    >
      <div className="max-w-3xl" ref={moduleRef}>
        {hasSubmitted ? (
          <SuccessScreen isInverted={isInverted} />
        ) : (
          <CaptureScreen
            acceptsMarketing={acceptsMarketing}
            disclaimer={disclaimer}
            handleMarketingMessageChange={handleMarketingMessageChange}
            handleOnChange={handleOnChange}
            handleSubmit={handleSubmit}
            isInverted={isInverted}
            isValid={isValid}
            marketingMessage={marketingMessage}
            setEmail={setEmail}
            setIsValid={setIsValid}
            text={text}
          />
        )}
      </div>
    </div>
  );
};

const CaptureScreen = ({
  text,
  isInverted,
  handleSubmit,
  isValid,
  handleOnChange,
  marketingMessage,
  acceptsMarketing,
  handleMarketingMessageChange,
  disclaimer,
}) => (
  <div
    className={cn(
      'flex w-full flex-col items-center justify-center gap-4 px-6 py-10 text-center sm:gap-6 sm:p-12',
      {
        'text-white': isInverted,
      }
    )}
  >
    <RichText content={text} className="[&>:last-child]:!mb-0" />
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row">
        <div className="w-full">
          <Input
            data-testid="email-input"
            inputType="text"
            className={cn('w-full', {
              '!border-b !border-white !bg-grey-dark !text-white': isInverted,
              '!bg-white': !isInverted,
            })}
            isValid={isValid}
            onChange={e => handleOnChange(e.target.value)}
            ariaLabel={I18n.localize('emailCapture.enterYourEmail')}
            placeholder={I18n.localize('emailCapture.enterYourEmail')}
          />
          {isValid === false && (
            <p className="text-left font-bold text-error">
              {I18n.localize('footer.emailSignUpErrorText')}
            </p>
          )}
        </div>
        <button
          data-testid="email-submit-button"
          type="submit"
          id="EmailSignupModule__submit-button"
          className={cn(
            `flex !h-12 cursor-pointer items-center justify-center whitespace-pre px-4 py-3
            text-sm font-bold uppercase tracking-[2px] transition-colors sm:max-w-fit`,
            {
              'bg-white text-gray-600 hover:bg-gray-500 hover:text-white': isInverted,
              'bg-black text-white hover:bg-white hover:text-black': !isInverted,
            }
          )}
        >
          {I18n.localize('emailCapture.signUp')}
        </button>
      </div>
    </form>
    {!isEmpty(marketingMessage) && (
      <Checkbox
        className="justify-center text-left sm:text-center"
        size={SIZES.SMALL}
        ariaLabel={marketingMessage}
        isChecked={acceptsMarketing}
        onChange={handleMarketingMessageChange}
        isInverted={isInverted}
      >
        {marketingMessage}
      </Checkbox>
    )}
    <RichText content={disclaimer} options={richTextOptions} />
  </div>
);

const SuccessScreen = ({ isInverted }) => (
  <div
    className={cn(
      'flex h-full w-full flex-col items-center justify-center gap-8 p-12 text-center',
      { 'text-white': isInverted }
    )}
  >
    <h2 className="text-2xl font-bold">{I18n.localize(`emailCapture.successHeader`)}</h2>
    <p>{I18n.localize(`emailCapture.successDescription`)}</p>
  </div>
);

EmailSignupModule.propTypes = {
  backgroundColor: PropTypes.string,
  disclaimer: PropTypes.object,
  homepagePosition: PropTypes.number,
  marketingMessage: PropTypes.string,
  moduleName: PropTypes.string,
  onSubmit: PropTypes.func,
  text: PropTypes.object,
  trackCTAClick: PropTypes.func,
};

CaptureScreen.propTypes = {
  acceptsMarketing: PropTypes.bool,
  disclaimer: PropTypes.object,
  handleMarketingMessageChange: PropTypes.func,
  handleOnChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isInverted: PropTypes.bool,
  isValid: PropTypes.bool,
  marketingMessage: PropTypes.string,
  setEmail: PropTypes.func,
  setIsValid: PropTypes.func,
  text: PropTypes.object,
};

SuccessScreen.propTypes = {
  isInverted: PropTypes.bool,
};

export default EmailSignupModule;
